import { Box, useTheme } from '@mui/material'
import React, { useEffect, useState } from 'react'

import { Swiper, SwiperSlide } from 'swiper/react'
import 'swiper/css'
import 'swiper/css/navigation'
import { A11y, Navigation, Pagination } from 'swiper/modules'

import { debounce } from '@/shared/lib'
import { debounceSpeedShort } from '@/shared/constants'
import { Arrow, VodArrowLeft, VodArrowRight } from '@/shared/ui'
import { Swiper as SwiperType } from 'swiper/types'
import { Season } from '@/types/vod/VodItemDetails'
import { FocusableSeason } from '@/features/VOD/components/FocusableSeason'

interface ISeasonSlider {
    seasons: Season[]
    actualSeason: Season | null | undefined
    handleSelectSeason: (season: Season) => void
}

export const SeasonSlider = ({
    seasons,
    actualSeason,
    handleSelectSeason,
}: ISeasonSlider) => {
    const theme = useTheme()

    const [swipe, setSwipe] = useState<SwiperType>()
    const [isAtStart, setIsAtStart] = useState(true)
    const [isAtEnd, setIsAtEnd] = useState(false)

    const handleSlideChange = () => {
        if (swipe) {
            setIsAtStart(swipe.isBeginning)
            setIsAtEnd(swipe.isEnd)
        }
    }

    useEffect(() => {
        handleSlideChange()
        if (swipe) {
            swipe.on('slideChange', handleSlideChange)
        }
        return () => {
            swipe?.off('slideChange', handleSlideChange)
        }
    }, [swipe])

    const leftClick = debounce(() => {
        swipe?.slidePrev()
        handleSlideChange()
    }, debounceSpeedShort)

    const rightClick = debounce(() => {
        swipe?.slideNext()
        handleSlideChange()
    }, debounceSpeedShort)

    const setSeasonBackgroundColor = (season: Season) =>
        season.id === actualSeason?.id ? 'white' : 'inherit'

    const setSeasonTextColor = (season: Season) =>
        season.id === actualSeason?.id ? 'black' : 'white'
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const seasonLabelStyle = {
        boxSizing: 'border-box',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '75px',
        height: '60px',
        padding: '16px',
        borderRadius: theme.shape.borderRadius,
    }

    const arrowWrapperStyle = (
        isAtEdge = false,
        direction: 'left' | 'right'
    ) => ({
        position: 'absolute',
        [direction]: 0,
        top: 0,
        width: '50px',
        opacity: isAtEdge ? 0 : 1,
        pointerEvents: 'all',
        cursor: 'pointer',
        height: '60px',
        svg: {
            height: '60px !important',
        },
    })

    const shadowBoxStyle = {
        position: 'absolute',
        width: '100%',
        height: '100%',
        background: 'black',
        opacity: 0.3,
        top: 0,
        left: 0,
        zIndex: 8,
        pointerEvents: 'all',
        cursor: 'pointer',
    }

    return (
        <Swiper
            key={`season-slider-${actualSeason?.id}`}
            onSwiper={(swiper: SwiperType) => setSwipe(swiper)}
            modules={[Navigation, Pagination, A11y]}
            spaceBetween={8}
            slidesPerGroup={2}
            slidesPerView="auto">
            {seasons.map((season) => (
                <SwiperSlide
                    key={season.seasonNo}
                    style={{ width: 'min-content' }}>
                    <FocusableSeason
                        season={season}
                        handleSelectSeason={handleSelectSeason}
                        setSeasonBackgroundColor={setSeasonBackgroundColor}
                        setSeasonTextColor={setSeasonTextColor}
                    />
                </SwiperSlide>
            ))}

            <Box position="absolute" top="0px" left="0px" width="100%">
                <Box
                    sx={arrowWrapperStyle(isAtStart, 'left')}
                    onClick={leftClick}>
                    <Arrow icon={<VodArrowLeft />} />
                    <Box sx={shadowBoxStyle} />
                </Box>
                <Box
                    sx={arrowWrapperStyle(isAtEnd, 'right')}
                    onClick={rightClick}>
                    <Arrow icon={<VodArrowRight />} />
                    <Box sx={shadowBoxStyle} />
                </Box>
            </Box>
        </Swiper>
    )
}
